// The shopping board: a lined notepad that sits beside a list, for writing the
// list by hand. Kept apart from the daily whiteboard (shared/whiteboard.ts):
// one board per list, never reset at midnight, cleared by hand. Ink uses the
// whiteboard's strokes; shortcut stamps (an icon and a name) sit on lines too.
//
// Every mark records the page and ruled line it sits on, so a line's strokes
// can be read back on their own (handwriting matching, later).

import { isStrokeId, parseStroke, WhiteboardInputError, type WhiteboardStroke } from "../whiteboard";
import type { KitchenList } from "./models";

/**
 * Page units: every screen draws into this space, scaled to fit. Wider than tall,
 * so the page fills a landscape wall (it was 1000 wide; older marks still fit).
 */
export const PAGE_W = 1300;
export const PAGE_H = 1150;
/** Where the first ruled line's row begins (room above for the heading). */
export const PAGE_TOP = 110;
export const LINE_H = 100;
export const LINES_PER_PAGE = 10;
/** The notepad's margin rule; stamps and writing start to its right. */
export const PAGE_MARGIN = 110;

export const BOARD_LIMITS = {
  pages: 20,
  marksPerBoard: 4000,
  marksPerRequest: 50,
  idsPerRequest: 4000,
  stampText: 60,
} as const;

export interface BoardStroke extends WhiteboardStroke {
  kind: "stroke";
  page: number;
  line: number;
}

export interface BoardStamp {
  kind: "stamp";
  id: string;
  page: number;
  line: number;
  /** A shortcut glyph key (src/widgets/kitchen/glyphs.tsx). */
  glyph: string;
  text: string;
  at: number;
}

export type BoardMark = BoardStroke | BoardStamp;

export interface ShoppingBoard {
  listId: string;
  /** The household's board change counter at this answer; ask with ?since= for changes only. */
  rev: number;
  marks: BoardMark[];
  /** With ?since=: marks erased since then. */
  removed: string[];
}

/** The household's shopping list: the one seeded on first use, or one named for shopping. */
export function isShoppingList(list: Pick<KitchenList, "id" | "name">): boolean {
  return list.id.endsWith("_groceries") || /grocer|shop/i.test(list.name);
}

/** The ruled line (0-based, on its page) a page-unit y falls on. */
export function lineAt(y: number): number {
  return Math.max(0, Math.min(LINES_PER_PAGE - 1, Math.floor((y - PAGE_TOP) / LINE_H)));
}

/** The line a stroke belongs to: where most of it sits (its median height). */
export function strokeLine(pts: number[]): number {
  const ys: number[] = [];
  for (let i = 1; i < pts.length; i += 3) ys.push(pts[i]!);
  ys.sort((a, b) => a - b);
  return lineAt(ys[Math.floor(ys.length / 2)] ?? PAGE_TOP);
}

/**
 * Where the next shortcut goes: the line after the last one used on this page,
 * or the first page after it with room. Null when every page is full.
 */
export function nextLine(marks: Iterable<Pick<BoardMark, "page" | "line">>, fromPage: number): { page: number; line: number } | null {
  const last = new Map<number, number>();
  for (const m of marks) last.set(m.page, Math.max(last.get(m.page) ?? -1, m.line));
  for (let page = Math.max(0, fromPage); page < BOARD_LIMITS.pages; page++) {
    const line = (last.get(page) ?? -1) + 1;
    if (line < LINES_PER_PAGE) return { page, line };
  }
  return null;
}

/** A page's marks grouped by line, strokes in the order they were drawn. */
export function marksByLine(marks: Iterable<BoardMark>, page: number): Map<number, BoardMark[]> {
  const out = new Map<number, BoardMark[]>();
  for (const m of [...marks].filter((m) => m.page === page).sort((a, b) => a.at - b.at || (a.id < b.id ? -1 : 1))) {
    const list = out.get(m.line) ?? [];
    list.push(m);
    out.set(m.line, list);
  }
  return out;
}

const GLYPH = /^[a-z][A-Za-z0-9]{0,30}$/;

function whole(v: unknown, min: number, max: number, what: string): number {
  if (typeof v !== "number" || !Number.isInteger(v) || v < min || v > max) throw new WhiteboardInputError(`Bad ${what}`);
  return v;
}

/** Validate one mark from a screen. A stroke's line is worked out from its points, whatever was sent. */
export function parseBoardMark(raw: unknown): BoardMark {
  if (!raw || typeof raw !== "object") throw new WhiteboardInputError("A mark must be an object");
  const r = raw as Record<string, unknown>;
  const page = whole(r.page, 0, BOARD_LIMITS.pages - 1, "page");
  if (r.kind === "stamp") {
    if (!isStrokeId(r.id)) throw new WhiteboardInputError("Bad mark id");
    if (typeof r.glyph !== "string" || !GLYPH.test(r.glyph)) throw new WhiteboardInputError("Unknown shortcut");
    const text = typeof r.text === "string" ? r.text.replace(/[\u0000-\u001f\u007f]/g, " ").replace(/\s+/g, " ").trim().slice(0, BOARD_LIMITS.stampText) : "";
    if (!text) throw new WhiteboardInputError("A shortcut needs a name");
    if (typeof r.at !== "number" || !Number.isFinite(r.at)) throw new WhiteboardInputError("Bad time");
    return { kind: "stamp", id: r.id, page, line: whole(r.line, 0, LINES_PER_PAGE - 1, "line"), glyph: r.glyph, text, at: Math.round(r.at) };
  }
  if (r.kind !== "stroke") throw new WhiteboardInputError("Unknown mark");
  const s = parseStroke(r);
  for (let i = 0; i < s.pts.length; i += 3) {
    if (s.pts[i]! < -50 || s.pts[i]! > PAGE_W + 50 || s.pts[i + 1]! < -50 || s.pts[i + 1]! > PAGE_H + 50) throw new WhiteboardInputError("Point out of range");
  }
  return { ...s, kind: "stroke", page, line: strokeLine(s.pts) };
}
